import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';

import { filter, map, of, switchMap, take, timeout } from 'rxjs';

import { AxleRegistrationOrchestratorService } from 'src/app/shared/services/public-events/service/public-registration-orchestrator.service';
import { PublicAttendeesStore } from 'src/app/shared/services/public-events/state/public-attendees.state';
import { PublicEventsStore } from 'src/app/shared/services/public-events/state/public-events.state';

export const determineActivitiesRedirect: CanActivateFn = (
  route: ActivatedRouteSnapshot
) => {
  const orchestrator = inject(AxleRegistrationOrchestratorService);
  const attendeeStore = inject(PublicAttendeesStore);
  const eventStore = inject(PublicEventsStore);
  const router = inject(Router);

  const userId: string | undefined = route.queryParams?.['userId'];
  const activitiesMap = toObservable(attendeeStore.strictAvailableEntityMap);

  if (!userId || !eventStore.eventDetails()) {
    const eventId = route.parent?.paramMap.get('id');
    router.navigate(['public', 'events', eventId], {
      queryParams: route.queryParams
    });
    return of(false);
  }

  attendeeStore.getAvailableActivities({
    payload: {
      userId
    },
    useCached: true
  });

  return activitiesMap.pipe(
    filter((map) => !!map?.[userId]),
    timeout({
      each: 10000,
      with: () => of(null)
    }),
    take(1),
    map((map) => map?.[userId]?.activities ?? []),
    switchMap((activities) =>
      activities.length
        ? of(true)
        : orchestrator.nextStep(
            eventStore.eventDetails()?.approvalType === 'SELF_BOOKING'
              ? 'SELECT_MEETINGS'
              : 'ACTIVITIES',
            userId
          )
    ),
    map(() => true)
  );
};

export const determineRequestableAdditionsRedirect: CanActivateFn = (
  route: ActivatedRouteSnapshot
) => {
  const orchestrator = inject(AxleRegistrationOrchestratorService);
  const attendeeStore = inject(PublicAttendeesStore);
  const eventStore = inject(PublicEventsStore);
  const router = inject(Router);

  const userId: string | undefined = route.queryParams?.['userId'];
  const additionsMap = toObservable(
    attendeeStore.strictAvailableRequestableAdditionsEntityMap
  );

  if (!userId || !eventStore.eventDetails()) {
    const eventId = route.parent?.paramMap.get('id');
    router.navigate(['public', 'events', eventId], {
      queryParams: route.queryParams
    });
    return of(false);
  }

  attendeeStore.getAvailableRequestableAdditions({
    payload: {
      userId
    },
    useCached: true
  });

  return additionsMap.pipe(
    filter((map) => !!map?.[userId]),
    timeout({
      each: 10000,
      with: () => of(null)
    }),
    take(1),
    map((map) => map?.[userId]?.additions ?? []),
    switchMap((additions) =>
      additions.length
        ? of(true)
        : orchestrator.nextStep('REQUESTABLE_ADDITIONS', userId)
    ),
    map(() => true)
  );
};

export const determineHostOrgGroupsRedirect: CanActivateFn = (
  route: ActivatedRouteSnapshot
) => {
  const orchestrator = inject(AxleRegistrationOrchestratorService);
  const attendeeStore = inject(PublicAttendeesStore);
  const eventStore = inject(PublicEventsStore);
  const router = inject(Router);

  const userId: string | undefined = route.queryParams?.['userId'];

  if (!userId || !eventStore.eventDetails()) {
    const eventId = route.parent?.paramMap.get('id');
    router.navigate(['public', 'events', eventId], {
      queryParams: route.queryParams
    });
    return of(false);
  }

  return attendeeStore.ensureHostOrgGroups$(userId).pipe(
    switchMap((groups) =>
      groups.length > 1
        ? of(true)
        : orchestrator.nextStep('SELECT_HOST_ORGANIZATIONS', userId)
    ),
    map(() => true)
  );
};
